const HOVER_QUERY = '(hover: hover) and (pointer: fine)'
const OPEN_DELAY = 80
const CLOSE_DELAY = 180

export class DelayedIntent {
  #timer
  #kind

  cancel(kind) {
    if (kind && kind !== this.#kind) return
    clearTimeout(this.#timer)
    this.#timer = undefined
    this.#kind = undefined
  }

  schedule(kind, callback, delay) {
    this.cancel()
    this.#kind = kind
    this.#timer = setTimeout(() => {
      this.#timer = undefined
      this.#kind = undefined
      callback()
    }, delay)
  }
}

if (globalThis.customElements && !customElements.get('site-navigation')) {
  class SiteNavigation extends HTMLElement {
    #abortController = null
    #menus = []
    #header = null
    #hoverMedia = window.matchMedia(HOVER_QUERY)
    #intent = new DelayedIntent()
    #hoverOpenedMenu = null

    connectedCallback() {
      this.#menus = Array.from(
        this.querySelectorAll(':scope details[data-navigation-menu]')
      )
      if (this.#menus.length === 0) return

      this.#abortController = new AbortController()
      const { signal } = this.#abortController
      this.#header = this.closest('.header')
      this.dataset.enhanced = ''

      for (const menu of this.#menus) {
        menu
          .querySelector(':scope > summary')
          ?.addEventListener('pointerenter', () => this.#onPointerEnter(menu), {
            passive: true,
            signal,
          })
      }
      this.#header?.addEventListener(
        'pointerleave',
        () => this.#scheduleClose(this.#openHoverMenu()),
        { passive: true, signal }
      )
      this.#header?.addEventListener(
        'pointerover',
        (event) => {
          if (
            !(event.target instanceof Element) ||
            !event.target.closest('[data-mega-menu-scrim]')
          ) {
            this.#intent.cancel('close')
          }
        },
        { capture: true, passive: true, signal }
      )
      this.#header?.addEventListener(
        'click',
        (event) => this.#onHeaderClick(event),
        { signal }
      )
      this.addEventListener('focusin', () => this.#intent.cancel(), { signal })
      document.addEventListener(
        'focusin',
        (event) => this.#onFocusIn(event),
        { signal }
      )
      this.addEventListener('click', (event) => this.#onClick(event), {
        signal,
      })
      const scrim = this.querySelector('[data-mega-menu-scrim]')
      scrim?.addEventListener(
        'pointerenter',
        () => this.#scheduleClose(this.#openHoverMenu()),
        { passive: true, signal }
      )
      document.addEventListener('keydown', (event) => this.#onKeyDown(event), {
        signal,
      })
    }

    disconnectedCallback() {
      this.#abortController?.abort()
      this.#abortController = null
      this.#header = null
      delete this.dataset.enhanced
      this.#intent.cancel()
      this.#hoverOpenedMenu = null
    }

    #onPointerEnter(menu) {
      if (!this.#usesHover(menu)) return

      this.#intent.cancel()
      if (menu.open) return
      this.#intent.schedule(
        'open',
        () => {
          const focusedMenu = this.#menus.find(
            (candidate) =>
              candidate.open &&
              candidate.name === menu.name &&
              candidate.matches(':focus-within')
          )
          if (focusedMenu && focusedMenu !== menu) {
            focusedMenu
              .querySelector(':scope > summary')
              ?.focus({ preventScroll: true })
          }
          this.#hoverOpenedMenu = menu
          menu.open = true
        },
        OPEN_DELAY
      )
    }

    #onClick(event) {
      const summary = event
        .composedPath()
        .find(
          (target) =>
            target instanceof HTMLElement && target.localName === 'summary'
        )
      const menu = summary?.parentElement
      if (
        !(menu instanceof HTMLDetailsElement) ||
        !menu.hasAttribute('data-navigation-menu')
      ) {
        return
      }

      this.#intent.cancel()
      const openedByHover = this.#hoverOpenedMenu === menu
      this.#hoverOpenedMenu = null
      if (event.detail > 0 && menu.open && openedByHover) {
        event.preventDefault()
      }
    }

    #onHeaderClick(event) {
      const openMenu = this.#menus.find((menu) => menu.open)
      const path = event.composedPath()
      if (!openMenu || path.includes(openMenu)) return

      const restoreFocus =
        openMenu.matches(':focus-within') &&
        path.some(
          (target) =>
            target instanceof Element &&
            target.hasAttribute('data-mega-menu-scrim')
        )
      this.#intent.cancel()
      this.#close(openMenu, restoreFocus)
    }

    /**
     * Reacts to focus arriving, not leaving: WebKit blurs a pressed link
     * without focusing anything, which would close the menu before its click.
     */
    #onFocusIn(event) {
      if (!(event.target instanceof Node)) return
      const menu = this.#menus.find(
        (candidate) => candidate.open && !candidate.contains(event.target)
      )
      if (menu) this.#close(menu)
    }

    #onKeyDown(event) {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      const eventMenu =
        event.target instanceof Element
          ? event.target.closest('details[data-navigation-menu][open]')
          : null
      const openMenu =
        eventMenu instanceof HTMLDetailsElement
          ? eventMenu
          : this.#menus.find((menu) => menu.open)
      if (!openMenu) return

      event.preventDefault()
      this.#close(openMenu, true)
    }

    #usesHover(menu) {
      return menu.dataset.trigger === 'hover' && this.#hoverMedia.matches
    }

    #openHoverMenu() {
      return this.#menus.find((menu) => menu.open && this.#usesHover(menu))
    }

    #scheduleClose(menu) {
      this.#intent.cancel()
      if (!menu) return
      this.#intent.schedule(
        'close',
        () => {
          if (menu.matches(':focus-within')) return
          this.#close(menu)
        },
        CLOSE_DELAY
      )
    }

    #close(menu, restoreFocus = false) {
      this.#hoverOpenedMenu = null
      for (const descendant of menu.querySelectorAll(
        ':scope details[data-navigation-menu][open]'
      )) {
        if (descendant instanceof HTMLDetailsElement) descendant.open = false
      }
      menu.open = false
      if (restoreFocus) {
        menu.querySelector(':scope > summary')?.focus({ preventScroll: true })
      }
    }
  }

  customElements.define('site-navigation', SiteNavigation)
}
